import { createSelector } from 'reselect'

import {
  getApiCallParticipants, getApiContacts,
  getApiParticipants,
  getApiTopics, getContactsLookup,
} from '../api/selectors'
import { getCurrentTeammateHandle } from '../current/selectors'

const makeGetTopicParticipants = () =>
  createSelector(
    [getApiTopics, getApiParticipants, (_, call) => call],
    (topics, participants, call) => {
      const topic = topics && topics[call?.topic_id]
      if (!topic) {
        return []
      }
      return topic.participants.map((participantId) => {
        return participants[participantId]
      }).filter(x => x)
    },
  )

const makeGetCallParticipantsByHandle = () =>
  createSelector(
    [getApiCallParticipants, (_, call) => call],
    (participants, call) => {
      const participantIds = call?.call_participants || []
      return participantIds.reduce((acc, id) => {
        const participant = participants[id]
        if (participant) {
          acc[participant.handle] = participant
        }
        return acc
      }, {})
    },
  )

const makeGetCallEnded = () =>
  createSelector([(_, call) => call], (call) => {
    return !!call?.end_time
  })

export const makeGetParticipantsGroupedByStatus = () => {
  const getTopicParticipants = makeGetTopicParticipants()
  const getCallParticipantsByHandle = makeGetCallParticipantsByHandle()
  const getCallEnded = makeGetCallEnded()
  return createSelector(
    [getTopicParticipants, getCallParticipantsByHandle, getCallEnded, getApiContacts, getContactsLookup],
    (topicParticipants, callParticipants, callEnded, contacts, contactsLookup) => {
      return topicParticipants
        .sort((a, b) => {
          const callParticipantA = callParticipants[a.handle]
          const callParticipantB = callParticipants[b.handle]
          if (!callParticipantA && !callParticipantB) {
            return Date.parse(a.created_at) - Date.parse(b.created_at)
          }
          if (callParticipantA && !callParticipantB) {
            return -1
          }
          if (!callParticipantA && callParticipantB) {
            return 1
          }
          return (
            Date.parse(callParticipantA.created_at) -
            Date.parse(callParticipantB.created_at)
          )
        })
        .reduce(
          (acc, participant) => {
            const callParticipant = callParticipants[participant.handle]
            const contact = contacts[contactsLookup[participant.handle]]
            if (callEnded) {
              if (callParticipant) {
                acc.wasOnCall.push({
                  topicParticipant: participant,
                  callParticipant: callParticipant,
                  contact,
                })
              }
            } else {
              if (callParticipant) {
                const { disconnected_at, left_call_at } = callParticipant
                if (left_call_at || disconnected_at) {
                  acc.notOnCall.push({
                    topicParticipant: participant,
                    callParticipant,
                    contact,
                  })
                }
                if (!left_call_at && !disconnected_at) {
                  acc.onCall.push({
                    topicParticipant: participant,
                    callParticipant,
                    contact,
                  })
                }
              } else {
                acc.notOnCall.push({
                  topicParticipant: participant,
                  callParticipant: undefined,
                  contact,
                })
              }
            }

            return acc
          },
          { onCall: [], notOnCall: [], wasOnCall: [] },
        )
    },
  )
}

export const makeGetCurrentCallParticipant = () =>
  createSelector(
    [
      getCurrentTeammateHandle,
      getApiCallParticipants,
      (_, props) => props.call,
    ],
    (currentTeammateHandle, participants, call) => {
      const recordParticipants = call?.call_participants
      if (
        !currentTeammateHandle ||
        !participants ||
        !recordParticipants?.length
      ) {
        return undefined
      }

      for (let i = 0; i < recordParticipants.length; i++) {
        const participantId = recordParticipants[i]
        const participant = participants[participantId]
        if (participant && participant.handle === currentTeammateHandle) {
          return participant
        }
      }
    },
  )

export const makeFetchParticipantByGIDParamSelector = () =>
  createSelector(
    [
      getApiCallParticipants,
      (_, props) => props.call,
      (_, props) => props.gidParam,
    ],
    (participants, call, gidParam) => {
      if (
        !participants ||
        !call ||
        !call?.call_participants?.length ||
        !gidParam
      )
        return undefined

      const identities = call.call_participants.reduce(
        (result, participantId) => {
          result[participantId] = participants[participantId].gid_param
          return result
        },
        {},
      )

      const participantId = Object.keys(identities).find(
        (key) => identities[key] === gidParam,
      )
      return participants[participantId]
    },
  )
